import { useAuth0 } from "@auth0/auth0-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import type { FirmsController } from "../../api/src/firms/firms.controller";
import type { NewFirmSignup } from "../../api/src/firms/firms.service";
import type { UnpackResponse } from "../../api/src/lib";
import Loading from "./Loading";
import Content from "./components/Content";
import { useErrorMessage } from "./components/FormError";
import FormFieldError from "./components/FormFieldError";
import MaskedInput from "./components/MaskedInput";
import SubmitButton from "./components/SubmitButton";
import AdvisorCodesEditor, {
  advisorCodeSchema,
} from "./firm/AdvisorCodeEditor";
import { usePublicdMutationAsync } from "./lib/api";
import {
  emailSchema,
  familyNameSchema,
  getSchemaFieldLabel,
  givenNameSchema,
  phoneNumberSchema,
} from "./lib/forms";
import { phoneMask } from "./lib/masks";

const schema: yup.ObjectSchema<NewFirmSignup> = yup.object({
  name: yup.string().required().label("Firm Name"),
  phoneNumber: phoneNumberSchema,
  supportEmail: emailSchema.required(),
  givenName: givenNameSchema.required(),
  familyName: familyNameSchema.required(),
  password: yup.string().min(12).required().label("Password"),
  advisorCodes: advisorCodeSchema,
  signupKey: yup.string().required().label("Signup Key"),
});

const SignUpSuccess = () => (
  <Content>
    <Row>
      <Col>
        <h2>Welcome to AdviceCloud!</h2>
        <p>
          Thank you for signing up. To get started, please review the{" "}
          <Link to="/client-onboarding">onboarding guide</Link> for step-by-step
          instructions on setting up your firm.
        </p>
        <p>
          If you have any questions, please contact us at{" "}
          <a href="mailto:support@advicecloudft.com">
            support@advicecloudft.com
          </a>
          .
        </p>
        <p>
          <small>
            The onboarding guide is also available under your Firm profile after
            logging in.
          </small>
        </p>
        <p>
          <strong>
            <Link to="/login">Continue to log in</Link>
          </strong>
        </p>
      </Col>
    </Row>
  </Content>
);

const SignUp = () => {
  // If we are already logged in, navigate to the home page.
  // Do not allow an existing user to create a new firm.
  const { isLoading, isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/");
    }
  }, [isLoading, isAuthenticated, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<NewFirmSignup>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const {
    fields: advisorCodes,
    append: appendAdvisorCode,
    remove: removeAdvisorCode,
    replace: replaceAdvisorCode,
  } = useFieldArray({
    name: "advisorCodes",
    control,
    keyName: "_field_id",
  });

  const { setError, resetError, errorMessage } = useErrorMessage();

  const createFirm = usePublicdMutationAsync<
    UnpackResponse<FirmsController["createFirm"]>
  >("/firm/signup", async (data: NewFirmSignup) => ({
    method: "POST",
    body: JSON.stringify(data),
  }));

  const onSubmit = useMutation({
    mutationFn: async (data: NewFirmSignup) => {
      resetError();

      try {
        await createFirm(data);
        setSignupResults({ isSuccess: true });
      } catch (err) {
        const message = "Sign Up failed";
        console.error(message, err);
        setError(message);
      }
    },
  });

  const [signupResults, setSignupResults] = useState<{
    isSuccess: boolean;
  }>({
    isSuccess: false,
  });

  return isLoading || isAuthenticated ? (
    <Loading />
  ) : signupResults.isSuccess ? (
    <SignUpSuccess />
  ) : (
    <Form onSubmit={handleSubmit((data) => onSubmit.mutateAsync(data))}>
      <Row>
        <Col>
          <h1>Sign Up</h1>
        </Col>
      </Row>
      <Content>
        {errorMessage}
        <Row>
          <Col>
            <Row>
              <Col lg={4} sm={12}>
                <Form.Group className="mb-3" controlId="form-signupKey">
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.signupKey)}
                  </Form.Label>
                  <Form.Control type="text" {...register("signupKey")} />
                  <Form.Text>
                    Enter the signup key provided by AdviceCloud.
                  </Form.Text>
                  <FormFieldError field={errors.signupKey} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg={4} sm={12}>
                <Form.Group className="mb-3" controlId="form-givenName">
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.givenName)}
                  </Form.Label>
                  <Form.Control type="text" {...register("givenName")} />
                  <FormFieldError field={errors.givenName} />
                </Form.Group>
              </Col>
              <Col lg={4} sm={12}>
                <Form.Group className="mb-3" controlId="form-familyName">
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.familyName)}
                  </Form.Label>
                  <Form.Control type="text" {...register("familyName")} />
                  <FormFieldError field={errors.familyName} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg={4} sm={12}>
                <Form.Group className="mb-3" controlId="form-supportEmail">
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.supportEmail)}
                  </Form.Label>
                  <Form.Control type="text" {...register("supportEmail")} />
                  <FormFieldError field={errors.supportEmail} />
                </Form.Group>
              </Col>
              <Col lg={4} sm={12}>
                <Form.Group className="mb-3" controlId="form-password">
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.password)}
                  </Form.Label>
                  <Form.Control type="password" {...register("password")} />
                  <Form.Text>
                    Password must be at least 12 letters long and contain at
                    least 1 uppercase letter, 1 lowercase letter, and 1 number.
                  </Form.Text>
                  <FormFieldError field={errors.password} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg={4} sm={12}>
                <Form.Group className="mb-3" controlId="form-name">
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.name)}
                  </Form.Label>
                  <Form.Control type="text" {...register("name")} />
                  <FormFieldError field={errors.name} />
                </Form.Group>
              </Col>
              <Col lg={4} sm={12}>
                <Form.Group className="mb-3" controlId="form-phoneNumber">
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.phoneNumber)}
                  </Form.Label>
                  <MaskedInput
                    name="phoneNumber"
                    type="tel"
                    mask={phoneMask.mask}
                    placeholder="+x (xxx)-xxx-xxxx"
                    control={control}
                  />
                  <FormFieldError field={errors.phoneNumber} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg={8} sm={12}>
                <h3>Master Accounts</h3>
                <AdvisorCodesEditor
                  fields={advisorCodes}
                  replace={replaceAdvisorCode}
                  add={appendAdvisorCode}
                  remove={removeAdvisorCode}
                  register={register}
                  errors={errors.advisorCodes}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col>
            <SubmitButton isSubmitting={isSubmitting} label="Sign Up" />
          </Col>
        </Row>
      </Content>
    </Form>
  );
};

export default SignUp;
